import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { allocationRulesApi, type AllocationRule } from '@/api/allocationRules';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { TabularText } from '@/components/TabularText';
import { useConfirm } from '@/components/useConfirm';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import { DryRunPanel, type DryRunSource } from './DryRunPanel';
import { RuleEditorDialog } from './RuleEditorDialog';
import { matchSummary, targetSummary, envelopeLabel } from './summarize';

const SKELETON_KEYS = ['a', 'b', 'c'] as const;

interface RulesTabProps {
  account: Account;
  writable: boolean;
  lapsed: boolean;
  readOnlyNote: boolean;
  operatingCurrency: string | null;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
}

/**
 * Account Rules tab (TMMAPP-0017): ordered payee-pattern rules, editor, reorder, dry run.
 * Writes are owner-only; a can_edit grantee sees the list plus `rules.readOnlyGrantee`.
 */
export function RulesTab({
  account,
  writable,
  lapsed,
  readOnlyNote,
  operatingCurrency,
  onNotify,
  onSubscriptionRequired,
}: RulesTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [rules, setRules] = useState<AllocationRule[] | null>(null);
  const [peerAccounts, setPeerAccounts] = useState<Account[]>([]);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [editor, setEditor] = useState<AllocationRule | 'new' | null>(null);
  const openNew = useSubscriberAction(() => setEditor('new'));
  const [dryRun, setDryRun] = useState<DryRunSource | null>(null);
  const deleting = useConfirm<AllocationRule>();
  const started = useRef(false);
  const timeZone = me.user.timezone ?? 'UTC';

  const load = useCallback(async () => {
    try {
      setRules(await allocationRulesApi.list(account.id));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [account.id]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void accountsApi
      .list()
      .then((list) => {
        if (!cancelled) setPeerAccounts(list.filter((a) => a.access === 'OWNER'));
      })
      .catch(() => {
        if (!cancelled) setPeerAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleEnabled(rule: AllocationRule, enabled: boolean) {
    setRules((prev) =>
      prev === null ? prev : prev.map((row) => (row.id === rule.id ? { ...row, enabled } : row)),
    );
    try {
      await allocationRulesApi.patch(account.id, rule.id, { enabled });
      await load();
    } catch (error) {
      setRules((prev) =>
        prev === null
          ? prev
          : prev.map((row) => (row.id === rule.id ? { ...row, enabled: rule.enabled } : row)),
      );
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else {
        onNotify(t('accounts.errorGeneric'));
      }
    }
  }

  async function handleReorder(nextIds: number[]) {
    try {
      await allocationRulesApi.reorder(account.id, nextIds);
      await load();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (isApiError(error) && error.code === ErrorCodes.REORDER_SET_MISMATCH) {
        onNotify(t('rules.reorderMismatch'));
        await load();
      } else {
        onNotify(t('accounts.errorGeneric'));
      }
    }
  }

  async function handleDelete() {
    await deleting.run(
      async (rule) => {
        await allocationRulesApi.remove(account.id, rule.id);
        onNotify(t('rules.deleted'));
        await load();
      },
      (error) => {
        if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
          onSubscriptionRequired();
          return null;
        }
        return t('accounts.errorGeneric');
      },
    );
  }

  const newRuleControl =
    lapsed || writable ? (
      <>
        <Button variant="contained" aria-describedby={openNew.descriptionId} onClick={openNew.run}>
          {t('rules.new')}
        </Button>
        <SubscriberActionHint id={openNew.descriptionId} description={openNew.description} />
      </>
    ) : null;

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t('errors.what.rules')}
        onRetry={() => {
          started.current = false;
          void load();
        }}
      />
    );
  }

  if (rules === null) {
    return (
      <Stack spacing={1} aria-busy="true">
        {SKELETON_KEYS.map((k) => (
          <Skeleton key={k} variant="rounded" height={40} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ alignItems: { md: 'flex-start' }, justifyContent: 'space-between', gap: 2, mb: 2 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h2">{t('rules.title')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('rules.intro')}
          </Typography>
          {readOnlyNote && <Typography variant="body2">{t('rules.readOnlyGrantee')}</Typography>}
        </Stack>
        {newRuleControl}
      </Stack>

      {rules.length === 0 && (
        <Stack spacing={2}>
          <Typography variant="body1">{t('rules.empty')}</Typography>
          {(lapsed || writable) && (
            <Box>
              <Button
                variant="contained"
                aria-describedby={openNew.descriptionId}
                onClick={openNew.run}
              >
                {t('rules.new')}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {rules.length > 0 &&
        (md ? (
          <Table size="small" aria-label={t('rules.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('rules.order')}</TableCell>
                <TableCell>{t('rules.name')}</TableCell>
                <TableCell>{t('rules.match')}</TableCell>
                <TableCell>{t('rules.targets')}</TableCell>
                <TableCell>{t('rules.enabled')}</TableCell>
                {writable && <TableCell>{t('rules.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule, index) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <TabularText>{index + 1}</TabularText>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{ color: rule.enabled ? 'text.primary' : 'text.secondary' }}
                    >
                      {rule.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{matchSummary(rule, operatingCurrency, locale, t)}</TableCell>
                  <TableCell>
                    <TargetsCell
                      targets={rule.targets}
                      currency={operatingCurrency}
                      locale={locale}
                      transferAccountId={rule.transferAccountId}
                      accounts={peerAccounts}
                    />
                  </TableCell>
                  <TableCell>
                    {writable ? (
                      <Switch
                        checked={rule.enabled}
                        onChange={(event) => void handleEnabled(rule, event.target.checked)}
                        slotProps={{ input: { 'aria-label': t('rules.enabled') } }}
                      />
                    ) : rule.enabled ? (
                      t('rules.enabled')
                    ) : null}
                  </TableCell>
                  {writable && (
                    <TableCell>
                      <RuleActions
                        index={index}
                        total={rules.length}
                        onEdit={() => setEditor(rule)}
                        onDryRun={() =>
                          setDryRun({
                            ruleId: rule.id,
                            transfer: rule.transferAccountId != null,
                          })
                        }
                        onMoveUp={() => void handleReorder(moveIds(rules, index, index - 1))}
                        onMoveDown={() => void handleReorder(moveIds(rules, index, index + 1))}
                        onMoveTop={() => void handleReorder(moveIds(rules, index, 0))}
                        onDelete={() => deleting.ask(rule)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Stack spacing={1.5}>
            {rules.map((rule, index) => (
              <Paper key={rule.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={0.75}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
                    <TabularText>{index + 1}</TabularText>
                    <Typography
                      variant="body1"
                      sx={{ color: rule.enabled ? 'text.primary' : 'text.secondary', flex: 1 }}
                    >
                      {rule.name}
                    </Typography>
                    {writable && (
                      <Switch
                        checked={rule.enabled}
                        onChange={(event) => void handleEnabled(rule, event.target.checked)}
                        slotProps={{ input: { 'aria-label': t('rules.enabled') } }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2">
                    {matchSummary(rule, operatingCurrency, locale, t)}
                  </Typography>
                  <TargetsCell
                    targets={rule.targets}
                    currency={operatingCurrency}
                    locale={locale}
                    transferAccountId={rule.transferAccountId}
                    accounts={peerAccounts}
                  />
                  {writable && (
                    <RuleActions
                      index={index}
                      total={rules.length}
                      onEdit={() => setEditor(rule)}
                      onDryRun={() =>
                        setDryRun({
                          ruleId: rule.id,
                          transfer: rule.transferAccountId != null,
                        })
                      }
                      onMoveUp={() => void handleReorder(moveIds(rules, index, index - 1))}
                      onMoveDown={() => void handleReorder(moveIds(rules, index, index + 1))}
                      onMoveTop={() => void handleReorder(moveIds(rules, index, 0))}
                      onDelete={() => deleting.ask(rule)}
                    />
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ))}

      {editor !== null && (
        <RuleEditorDialog
          accountId={account.id}
          currency={operatingCurrency}
          timeZone={timeZone}
          rule={editor === 'new' ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            onNotify(message);
            void load();
          }}
        />
      )}
      {dryRun !== null && (
        <DryRunPanel
          accountId={account.id}
          currency={operatingCurrency}
          timeZone={timeZone}
          source={dryRun}
          onClose={() => setDryRun(null)}
        />
      )}
      {deleting.target !== null && (
        <ConfirmDialog
          title={t('rules.deleteTitle', { name: deleting.target.name })}
          consequence={t('rules.deleteBody')}
          confirmLabel={t('rules.deleteRule')}
          destructive
          busy={deleting.busy}
          error={deleting.error}
          onConfirm={() => void handleDelete()}
          onCancel={deleting.cancel}
        />
      )}
    </Box>
  );
}

function TargetsCell({
  targets,
  currency,
  locale,
  transferAccountId = null,
  accounts = [],
}: {
  targets: AllocationRule['targets'];
  currency: string | null;
  locale: string;
  transferAccountId?: number | null;
  accounts?: { id: number; name: string }[];
}) {
  const { t } = useTranslation();
  const { envelopes } = useOwnedEnvelopes();
  if (transferAccountId != null) {
    const name = accounts.find((a) => a.id === transferAccountId)?.name ?? `#${transferAccountId}`;
    return (
      <Typography variant="body2" component="div">
        {t('rules.transferTo', { account: name })}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" component="div">
      {targets.map((row, index) => {
        const { unknown } = envelopeLabel(envelopes, row.envelopeId);
        const text = targetSummary(row, envelopes, currency, locale);
        return (
          <span key={`${row.envelopeId}-${index}`}>
            {index > 0 ? ' · ' : ''}
            {unknown ? (
              <Typography variant="mono" component="span">
                {text}
              </Typography>
            ) : (
              text
            )}
          </span>
        );
      })}
    </Typography>
  );
}

function moveIds(rules: AllocationRule[], from: number, to: number): number[] {
  const ids = rules.map((r) => r.id);
  if (to < 0 || to >= ids.length || from === to) return ids;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return ids;
  next.splice(to, 0, moved);
  return next;
}

function RuleActions({
  index,
  total,
  onEdit,
  onDryRun,
  onMoveUp,
  onMoveDown,
  onMoveTop,
  onDelete,
}: {
  index: number;
  total: number;
  onEdit: () => void;
  onDryRun: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onMoveTop: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
      <Button size="small" onClick={onEdit}>
        {t('rules.edit')}
      </Button>
      <Button size="small" onClick={onDryRun}>
        {t('rules.dryRun')}
      </Button>
      <Button size="small" onClick={onMoveUp} disabled={index === 0}>
        {t('rules.moveUp')}
      </Button>
      <Button size="small" onClick={onMoveDown} disabled={index === total - 1}>
        {t('rules.moveDown')}
      </Button>
      <Button size="small" onClick={onMoveTop} disabled={index === 0}>
        {t('rules.moveTop')}
      </Button>
      <Button size="small" color="error" onClick={onDelete}>
        {t('rules.delete')}
      </Button>
    </Stack>
  );
}
